import {
  BoxGeometry,
  type BufferGeometry,
  Color,
  CylinderGeometry,
  Group,
  type Material,
  Mesh,
  Object3D,
  SphereGeometry,
} from 'three';
import { EMIT, SURFACE } from '../render/palette';
import { applyContactShading, emitter, flatVertexColor, surface } from '../render/materials';
import { markRig } from './merge-static';
import { BALANCE } from '../sim/balance';

/**
 * THE OTHER PEOPLE IN THE BUILDING.
 *
 * This is what a teammate looks like. It is not a re-skinned guard, and the
 * differences are all load-bearing.
 *
 * A guard is read as a threat: dark, cold, banded in retroreflective yellow,
 * carrying a light. A thief has to be read as a friend at a glance and from
 * behind, in a dark mall, while you are running — which is a harder problem,
 * because the honest answer to "who is that" cannot be a name tag you have to
 * stop and squint at. So it is carried by three things, in the order you resolve
 * them at decreasing distance:
 *
 *  1. HUE. The kit is warm-dark where the uniform is blue-dark. Every other
 *     surface in the building is neutral grey, so at fifty metres the only
 *     question the silhouette has to answer — friend or guard — is answered by
 *     temperature alone, before any detail arrives.
 *  2. THE PACK. It grows. A thief carrying nothing has a flat back; a thief at
 *     the weight limit is visibly hunched under a box half their torso deep and
 *     leaning into it. That is the encumbrance number, drawn on a body, and it
 *     means "who is loaded and needs to bank" is answerable across a food court
 *     without a word on the radio.
 *  3. THE BAND. One emissive armband, per-slot hue, dim enough that it is a
 *     confirmation rather than a lantern. This is the last thing to resolve and
 *     the only one that gives you a specific person.
 *
 * The face is a void inside the hood with a pale mannequin jaw under it. The
 * building is full of mannequins and every one of them has that jaw; putting it
 * on the crew is the one place the game is allowed to be openly unpleasant about
 * what these people are.
 */

const M = BALANCE.move;

/** Stride cycles per metre. Shared with the guard so the two gaits agree. */
const STRIDE = 0.62;

/**
 * Per-slot armband hues, drawn from the sign palette so nothing new enters the
 * frame. Ordered by how well they separate from each other under the blue grade,
 * not by any prettier logic — the first four are the ones a four-person crew
 * actually gets.
 */
export const CREW_COLORS: readonly Color[] = [
  EMIT.signCyan,
  EMIT.signAmber,
  EMIT.signMagenta,
  EMIT.exitGreen,
  EMIT.hiVis,
  EMIT.crt,
  EMIT.potionA,
  EMIT.potionB,
];

export function crewColor(slot: number): Color {
  return CREW_COLORS[((slot % CREW_COLORS.length) + CREW_COLORS.length) % CREW_COLORS.length];
}

export interface ThiefPose {
  /** Ground speed in m/s. Drives the stride. */
  speed: number;
  dt: number;
  /** Head yaw and pitch relative to the body, radians. */
  headYaw: number;
  headPitch: number;
  /** 0 standing, 1 fully crouched. */
  crouch: number;
  /** 0..1, how full the pack is. Visible from behind, which is the point. */
  load: number;
  /** 0..1, working a lock or a case. Both hands go forward and shake. */
  working: number;
  /** 0..1, caught and on the floor. */
  down: number;
}

export class ThiefFigure {
  readonly root = new Group();

  private readonly hips = new Group();
  private readonly torso = new Group();
  private readonly neck = new Group();
  private readonly legL = new Group();
  private readonly legR = new Group();
  private readonly armL = new Group();
  private readonly armR = new Group();
  private readonly pack: Mesh;
  private readonly band: Mesh;
  private phase = 0;
  private lean = 0;
  private packFill = 0;
  private shake = 0;

  constructor(
    readonly id: string,
    slot: number,
  ) {
    // Named, so `mergeStatic` leaves the subtree alone. A player folded into a
    // static batch is a player welded to the floor. The name is not enough on
    // its own — every OTHER named group gets its anonymous interior merged, and
    // a thief's forearm is anonymous and still moves. See `markRig`.
    this.root.name = `thief:${id}`;
    markRig(this.root);
    this.root.add(this.hips);
    this.hips.add(this.torso, this.legL, this.legR);
    this.torso.add(this.neck, this.armL, this.armR);

    const coat = surface('thiefCoat', SURFACE.thiefCoat, { roughness: 0.95 });
    const coatDark = surface('thiefCoatDark', SURFACE.thiefCoatDark, { roughness: 0.96 });
    const trouser = surface('thiefTrouser', SURFACE.thiefTrouser, { roughness: 0.93 });
    const strap = surface('thiefStrap', SURFACE.thiefStrap, { roughness: 0.88 });
    const skin = surface('mannequin', SURFACE.mannequin, { roughness: 0.78 });
    const rubber = surface('rubber', SURFACE.rubber, { roughness: 0.99 });

    // --- legs ---------------------------------------------------------------
    for (const [group, side] of [
      [this.legL, -1],
      [this.legR, 1],
    ] as const) {
      group.position.set(side * 0.13, 0.88, 0);
      const thigh = mesh(new CylinderGeometry(0.098, 0.084, 0.48, 7), trouser, 0.5);
      thigh.position.y = -0.24;
      const shin = mesh(new CylinderGeometry(0.084, 0.07, 0.44, 7), trouser, 0.4);
      shin.position.y = -0.68;
      // Soft-soled. A detail nobody will consciously see, and the reason the
      // footstep cues in the audio layer are the quiet set.
      const boot = mesh(new BoxGeometry(0.13, 0.1, 0.26), rubber, 0);
      boot.position.set(0, -0.87, 0.03);
      group.add(thigh, shin, boot);
    }

    // --- torso --------------------------------------------------------------
    this.torso.position.y = 0.88;
    const chest = mesh(new BoxGeometry(0.4, 0.5, 0.23), coat, 0.5);
    chest.position.y = 0.25;
    const waist = mesh(new BoxGeometry(0.35, 0.16, 0.21), coatDark, 0.1);
    waist.position.y = -0.04;
    this.torso.add(chest, waist);

    // Chest webbing. Two straps in a narrow V, which is the shape that says
    // "carrying something" before the pack behind it is even visible.
    for (const side of [-1, 1]) {
      const s = mesh(new BoxGeometry(0.05, 0.42, 0.245), strap, 0);
      s.position.set(side * 0.1, 0.26, 0.002);
      s.rotation.z = side * 0.1;
      this.torso.add(s);
    }
    const belt = mesh(new BoxGeometry(0.38, 0.06, 0.23), strap, 0);
    belt.position.y = -0.1;
    this.torso.add(belt);

    // --- the pack -----------------------------------------------------------
    // Anchored at the spine and scaled from there, so filling it pushes the mass
    // backwards and up rather than growing symmetrically through the ribcage.
    this.pack = mesh(new BoxGeometry(0.34, 0.42, 0.24), strap, 0.3);
    this.pack.position.set(0, 0.26, 0.235);
    this.pack.scale.set(0.86, 0.5, 0.18);
    this.torso.add(this.pack);

    // --- head ---------------------------------------------------------------
    this.neck.position.y = 0.56;
    // The jaw. Pale, and the only skin on the model.
    const jaw = mesh(new SphereGeometry(0.098, 8, 6), skin, 0);
    jaw.scale.set(1, 1.1, 0.94);
    jaw.position.y = 0.11;
    // The hood, as a slightly larger shell sitting over the head with its front
    // face open. Because it is a full sphere scaled long and pushed back, the
    // brow overhangs the jaw and the eyes are simply absent.
    const hood = mesh(new SphereGeometry(0.128, 8, 6), coatDark, 0);
    hood.scale.set(1, 1.06, 1.08);
    hood.position.set(0, 0.135, 0.028);
    // The gorget: a collar ring that hides the neck seam and reads, from behind,
    // as the drawstring bunch of a hood pulled tight.
    const collar = mesh(new CylinderGeometry(0.115, 0.135, 0.09, 8), coat, 0);
    collar.position.y = 0.03;
    this.neck.add(jaw, hood, collar);

    // --- arms ---------------------------------------------------------------
    for (const [group, side] of [
      [this.armL, -1],
      [this.armR, 1],
    ] as const) {
      group.position.set(side * 0.24, 0.4, 0);
      const upper = mesh(new CylinderGeometry(0.056, 0.048, 0.34, 6), coat, 0.3);
      upper.position.y = -0.17;
      const fore = mesh(new CylinderGeometry(0.048, 0.043, 0.32, 6), coatDark, 0.2);
      fore.position.y = -0.49;
      const glove = mesh(new BoxGeometry(0.085, 0.11, 0.075), rubber, 0);
      glove.position.y = -0.68;
      group.add(upper, fore, glove);
    }

    // --- the band -----------------------------------------------------------
    // 0.55 rather than the guards' hi-vis 0.5 because it is a quarter the area,
    // and dim enough on purpose: this is a confirmation, not a lantern, and a
    // crew that glows across the atrium is a crew that has given up on the dark.
    this.band = new Mesh(
      flatVertexColor(new CylinderGeometry(0.062, 0.062, 0.055, 8, 1, true)),
      emitter(`crew${slot % CREW_COLORS.length}`, crewColor(slot), 0.55),
    );
    this.band.position.y = -0.1;
    this.armL.add(this.band);
  }

  /** How full the pack looks right now. Eased, so picking up is not a pop. */
  get packLevel(): number {
    return this.packFill;
  }

  /** Where a name tag or a voice indicator should float. World space. */
  headWorld(out: Object3D): void {
    this.neck.getWorldPosition(out.position);
  }

  pose(p: ThiefPose): void {
    // Stride follows distance covered, not time, so slowing to a stop does not
    // moonwalk the last half second.
    this.phase += p.speed * p.dt * STRIDE * Math.PI * 2;
    const crouch = clamp(p.crouch, 0, 1);
    const swing = Math.min(1, p.speed / M.runSpeed) * 0.85 * (1 - crouch * 0.45);
    const s = Math.sin(this.phase);
    const c = Math.cos(this.phase);

    this.legL.rotation.x = s * swing;
    this.legR.rotation.x = -s * swing;

    // The pack eases towards the real load. Snapping it on pickup would make a
    // fifty-kilo television look like it inflated.
    this.packFill += (clamp(p.load, 0, 1) - this.packFill) * Math.min(1, p.dt * 3);
    const f = this.packFill;
    this.pack.scale.set(0.86 + f * 0.2, 0.5 + f * 0.62, 0.18 + f * 0.86);
    this.pack.position.set(0, 0.26 - f * 0.03, 0.235 + f * 0.11);

    if (p.working > 0.01) {
      // Both hands forward and low, with a fast tremor scaled by how hard the
      // tool is working. Deliberately not a clean animation: the read is
      // "someone is straining at something", and clean looks like a wave.
      this.shake += p.dt * 34;
      const w = clamp(p.working, 0, 1);
      const tremor = Math.sin(this.shake) * 0.06 * w;
      for (const arm of [this.armL, this.armR]) {
        arm.rotation.x = -1.05 * w + tremor;
        arm.rotation.z = 0;
      }
      this.armL.rotation.z = 0.22 * w;
      this.armR.rotation.z = -0.22 * w;
    } else {
      this.armL.rotation.x = s * swing * 0.8;
      this.armR.rotation.x = -s * swing * 0.8;
      // Arms held wider as the pack fills, because they physically cannot hang
      // straight past a full one. Free character, one line.
      this.armL.rotation.z = 0.08 + f * 0.12;
      this.armR.rotation.z = -0.08 - f * 0.12;
    }

    // Hips rise twice per stride, roll onto the loaded leg, and sink with crouch.
    this.hips.position.y = Math.abs(c) * 0.035 * swing - crouch * 0.42;
    this.hips.rotation.z = s * 0.05 * swing;
    // Knees have to fold or a crouching figure is a standing one buried in the
    // floor up to the shin.
    this.legL.rotation.x += crouch * 0.5;
    this.legR.rotation.x += crouch * 0.5;

    // Lean: into the run, further under load, further again when crouched. The
    // three stack, so a loaded thief sprinting in a crouch is almost horizontal,
    // which is both correct and very funny.
    const wantLean = Math.min(1, p.speed / M.runSpeed) * 0.16 + f * 0.14 + crouch * 0.3;
    this.lean += (wantLean - this.lean) * Math.min(1, p.dt * 4);
    this.torso.rotation.x = this.lean;

    // The head stays level while the body leans, which is what necks are for and
    // what stops a sprinting figure from studying its own feet.
    this.neck.rotation.y = clamp(p.headYaw, -1.2, 1.2);
    this.neck.rotation.x = clamp(p.headPitch, -0.6, 0.6) - this.lean;

    if (p.down > 0) {
      this.root.rotation.x = -p.down * Math.PI * 0.46;
      this.root.position.y = -p.down * 0.1;
      this.legL.rotation.x = p.down * 0.55;
      this.legR.rotation.x = p.down * 0.3;
      this.armL.rotation.x = p.down * 1.0;
      this.armR.rotation.x = p.down * 1.2;
    } else if (this.root.rotation.x !== 0) {
      this.root.rotation.x = 0;
      this.root.position.y = 0;
    }
  }

  /** Hidden rather than removed, so a respawn does not rebuild 20 meshes. */
  setVisible(v: boolean): void {
    this.root.visible = v;
  }

  dispose(): void {
    this.root.removeFromParent();
    this.root.traverse((o) => {
      if (o instanceof Mesh) o.geometry.dispose();
    });
  }
}

function mesh(geo: BufferGeometry, mat: Material, contact: number): Mesh {
  if (contact > 0) applyContactShading(geo, { falloff: contact, strength: 0.32 });
  else flatVertexColor(geo);
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
